import { diagnosticAPI } from './api';
import { DiagnosticStatus } from '../components/DiagnosticCard';

export interface DiagnosticResult {
  title: string;
  status: DiagnosticStatus;
  message: string;
  data?: any;
}

export interface MessageTestResult {
  status: 'success' | 'failed';
  messageId?: string;
  deliveryTime?: string;
  carrier?: string;
  timestamp?: string;
  error?: string;
  skipped?: boolean;
}

class DiagnosticService {
  private campaignId: string | null = null;

  private findNestedValue(obj: any, key: string): any {
    if (obj === null || obj === undefined) return undefined;
    
    if (typeof obj === 'object') {
      if (key in obj) return obj[key];
      
      for (const k in obj) {
        const result = this.findNestedValue(obj[k], key);
        if (result !== undefined) return result;
      }
    }
    
    return undefined;
  }

  async runAllDiagnostics(phoneNumber: string, destinationNumber?: string): Promise<DiagnosticResult[]> {
    console.log('🔍 Starting diagnostics for phone number:', phoneNumber);
    if (destinationNumber) {
      console.log('📞 Destination number:', destinationNumber);
    }
    
    const results: DiagnosticResult[] = [];
    
    try {
      // Run initial diagnostics in parallel
      const [
        numberMappingResult,
        smsEnablementResult,
        recentErrorsResult
      ] = await Promise.all([
        this.checkNumberMapping(phoneNumber),
        this.checkSmsEnablement(phoneNumber),
        this.checkRecentErrors(phoneNumber, destinationNumber)
      ]);
      
      results.push(
        numberMappingResult,
        smsEnablementResult,
        recentErrorsResult
      );
      
      // Run campaign status and carrier approval checks synchronously after SMS status check
      // These depend on the campaignId that should be set during SMS enablement check
      console.log('🔍 Running campaign status and carrier approval checks after SMS status check...');
      const campaignStatusResult = await this.checkCampaignStatus();
      const carrierApprovalResult = await this.checkCarrierApproval();
      
      results.push(
        campaignStatusResult,
        carrierApprovalResult
      );
      
      console.log('✅ All diagnostics completed');
      return results;
    } catch (error) {
      console.error('❌ Error running diagnostics:', error);
      throw error;
    }
  }

  private async checkNumberMapping(phoneNumber: string): Promise<DiagnosticResult> {
    try {
      const response = await diagnosticAPI.getNumberMapping(phoneNumber);
      
      if (response.data) {
        // Extract campaignId for later use
        this.campaignId = response.data.campaignId;
        
        return {
          title: 'Number Mapping Check',
          status: 'success',
          message: `Number mapped to ${response.data.messagingAppType} for ${response.data.name || 'Unknown'}`,
          data: {
            platform: response.data.messagingAppType,
            name: response.data.name,
            userId: response.data.messagingAppId,
            enterpriseId: response.data.enterpriseId,
            enterpriseName: response.data.enterpriseName,
            cspId: response.data.cspId,
          }
        };
      } else {
        return {
          title: 'Number Mapping Check',
          status: 'failed',
          message: 'Number not mapped in the system',
          data: response.data || {
            phoneNumber,
            error: 'No response data from API',
            errorType: 'Empty Response'
          }
        };
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          title: 'Number Mapping Check',
          status: 'failed',
          message: 'Number not mapped in the system',
          data: {
            phoneNumber,
            errorCode: 404,
            errorType: 'Not Found',
            error: error.response?.data?.message || 'Number not found in database'
          }
        };
      }
      return {
        title: 'Number Mapping Check',
        status: 'error',
        message: 'Error checking number mapping',
        data: {
          phoneNumber,
          error: error.message,
          errorType: 'Network/API Error',
          errorCode: error.response?.status || 'Unknown'
        }
      };
    }
  }

  private async checkSmsEnablement(phoneNumber: string): Promise<DiagnosticResult> {
    try {
      console.log('🔍 SMS Enablement Step 1: Calling /sms-status for', phoneNumber);
      const response = await diagnosticAPI.getSmsStatus(phoneNumber);
      
      // Handle JSON response from Flask server
      const smsStatusData = response.data;
      console.log('🔍 SMS Status Response:', smsStatusData);
      
      // Extract values from JSON response
      const status = smsStatusData?.Status;
      const accountId = smsStatusData?.AccountId;
      const orderId = smsStatusData?.OrderId;
      
      console.log('📊 SMS Status Parsed Data:', {
        status,
        accountId,
        orderId,
        rawData: smsStatusData
      });
      
      // Check if status is "Inservice" (case-insensitive)
      if (status?.toLowerCase() === 'inservice') {
        console.log('✅ Status is Inservice, checking SMS enablement...');
        
        // Additional check for SMS enablement using accountId and orderId
        try {
          console.log('🔍 SMS Enablement Step 2: Calling /sms-enabled with', {
            accountId,
            orderId
          });
          
          // Validate we have the required data
          if (!accountId || !orderId) {
            console.error('❌ Missing AccountId or OrderId:', {
              accountId,
              orderId
            });
            return {
              title: 'SMS Enablement',
              status: 'failed',
              message: 'SMS not enabled for this number - missing account information',
              data: {
                status,
                accountId,
                orderId
              }
            };
          }
          
          const smsEnabledResponse = await diagnosticAPI.getSmsProcessingStatus(
            accountId,
            orderId,
            phoneNumber
          );
          
          // Handle JSON response from Flask server
          const smsEnabledData = smsEnabledResponse.data;
          console.log('🔍 SMS Enabled Response:', smsEnabledData);
          
          // Log which response structure we're dealing with
          if (smsEnabledData?.Sms) {
            console.log('📋 Processing first API response structure (direct SMS field)');
          } else if (smsEnabledData?.TnOptionOrder?.TnOptionGroups?.TnOptionGroup) {
            console.log('📋 Processing first API response structure (nested TnOptionOrder)');
          } else if (smsEnabledData?.TelephoneNumberDetails?.MessagingSettings?.SmsEnabled) {
            console.log('📋 Processing fallback API response structure (TN Details API)');
          } else {
            console.log('📋 Processing unknown response structure, using fallback search');
          }
          
          // Extract SMS value and CampaignId from JSON response
          let smsValue, campaignId;
          
          // Add detailed logging for campaignId extraction
          console.log('🔍 Starting Campaign ID Extraction Process...');
          console.log('🔍 Response Structure Analysis:', {
            hasDirectSms: !!smsEnabledData?.Sms,
            hasTnOptionOrder: !!smsEnabledData?.TnOptionOrder,
            hasTnOptionGroups: !!smsEnabledData?.TnOptionOrder?.TnOptionGroups,
            hasTnOptionGroup: !!smsEnabledData?.TnOptionOrder?.TnOptionGroups?.TnOptionGroup,
            hasTelephoneNumberDetails: !!smsEnabledData?.TelephoneNumberDetails,
            hasMessagingSettings: !!smsEnabledData?.TelephoneNumberDetails?.MessagingSettings,
            hasSmsEnabled: !!smsEnabledData?.TelephoneNumberDetails?.MessagingSettings?.SmsEnabled
          });
          
          // Handle different possible JSON structures
          if (smsEnabledData?.Sms) {
            // Direct SMS field (first API response structure)
            console.log('📋 Processing: Direct SMS field structure');
            smsValue = smsEnabledData.Sms;
            campaignId = smsEnabledData.CampaignId;
            console.log('🔍 Direct SMS - smsValue:', smsValue, 'campaignId:', campaignId);
            console.log('🔍 Direct SMS - smsEnabledData.CampaignId:', smsEnabledData.CampaignId);
          } else if (smsEnabledData?.TnOptionOrder?.TnOptionGroups?.TnOptionGroup) {
            // Nested structure (first API response structure)
            console.log('📋 Processing: Nested TnOptionOrder structure');
            const tnOptionGroup = smsEnabledData.TnOptionOrder.TnOptionGroups.TnOptionGroup;
            console.log('🔍 TnOptionGroup data:', tnOptionGroup);
            console.log('🔍 TnOptionGroup type:', Array.isArray(tnOptionGroup) ? 'Array' : 'Object');
            
            if (Array.isArray(tnOptionGroup)) {
              console.log('🔍 Array TnOptionGroup - First element:', tnOptionGroup[0]);
              console.log('🔍 Array TnOptionGroup - First element SMS:', tnOptionGroup[0]?.Sms);
              console.log('🔍 Array TnOptionGroup - First element A2pSettings:', tnOptionGroup[0]?.A2pSettings);
              console.log('🔍 Array TnOptionGroup - First element CampaignId:', tnOptionGroup[0]?.A2pSettings?.CampaignId);
              
              smsValue = tnOptionGroup[0]?.Sms;
              campaignId = tnOptionGroup[0]?.A2pSettings?.CampaignId;
            } else {
              console.log('🔍 Object TnOptionGroup - SMS:', tnOptionGroup?.Sms);
              console.log('🔍 Object TnOptionGroup - A2pSettings:', tnOptionGroup?.A2pSettings);
              console.log('🔍 Object TnOptionGroup - CampaignId:', tnOptionGroup?.A2pSettings?.CampaignId);
              
              smsValue = tnOptionGroup?.Sms;
              campaignId = tnOptionGroup?.A2pSettings?.CampaignId;
            }
            
            console.log('🔍 Nested Structure - Final smsValue:', smsValue, 'campaignId:', campaignId);
            console.log('🔍 Nested Structure - A2pSettings path:', smsEnabledData?.TnOptionOrder?.TnOptionGroups?.TnOptionGroup?.A2pSettings);
            console.log('🔍 Nested Structure - CampaignId path:', smsEnabledData?.TnOptionOrder?.TnOptionGroups?.TnOptionGroup?.A2pSettings?.CampaignId);
            
          } else if (smsEnabledData?.TelephoneNumberDetails?.MessagingSettings?.SmsEnabled) {
            // Fallback API response structure (TN Details API)
            console.log('📋 Processing: Fallback TN Details API structure');
            console.log('🔍 TelephoneNumberDetails:', smsEnabledData.TelephoneNumberDetails);
            console.log('🔍 MessagingSettings:', smsEnabledData.TelephoneNumberDetails.MessagingSettings);
            console.log('🔍 SmsEnabled:', smsEnabledData.TelephoneNumberDetails.MessagingSettings.SmsEnabled);
            console.log('🔍 CampaignId from MessagingSettings:', smsEnabledData.TelephoneNumberDetails.MessagingSettings.CampaignId);
            
            smsValue = smsEnabledData.TelephoneNumberDetails.MessagingSettings.SmsEnabled;
            campaignId = smsEnabledData.TelephoneNumberDetails.MessagingSettings.CampaignId;
            
            console.log('🔍 Fallback API - Final smsValue:', smsValue, 'campaignId:', campaignId);
          } else {
            // Fallback - look for SMS field anywhere in the object
            console.log('📋 Processing: Fallback search for SMS field');
            smsValue = this.findNestedValue(smsEnabledData, 'Sms');
            campaignId = this.findNestedValue(smsEnabledData, 'CampaignId');
            console.log('🔍 Fallback Search - smsValue:', smsValue, 'campaignId:', campaignId);
          }
          
          // Add comprehensive campaignId validation logging
          console.log('🔍 Campaign ID Extraction Summary:');
          console.log('  - smsValue:', smsValue, '(type:', typeof smsValue, ')');
          console.log('  - campaignId:', campaignId, '(type:', typeof campaignId, ')');
          console.log('  - campaignId is truthy:', !!campaignId);
          console.log('  - campaignId length:', campaignId ? campaignId.length : 'N/A');
          console.log('  - campaignId trimmed:', campaignId ? campaignId.trim() : 'N/A');
          
          // Log the full response structure for debugging
          console.log('🔍 Full Response Structure for Campaign ID Debug:');
          if (smsEnabledData?.TnOptionOrder?.TnOptionGroups?.TnOptionGroup) {
            const tnOptionGroup = smsEnabledData.TnOptionOrder.TnOptionGroups.TnOptionGroup;
            if (Array.isArray(tnOptionGroup)) {
              console.log('  - TnOptionGroup[0]:', JSON.stringify(tnOptionGroup[0], null, 2));
            } else {
              console.log('  - TnOptionGroup:', JSON.stringify(tnOptionGroup, null, 2));
            }
          }
          if (smsEnabledData?.TelephoneNumberDetails?.MessagingSettings) {
            console.log('  - MessagingSettings:', JSON.stringify(smsEnabledData.TelephoneNumberDetails.MessagingSettings, null, 2));
          }
          
          console.log('📊 SMS Enabled Parsed Data:', {
            smsValue,
            campaignId,
            responseStructure: smsEnabledData?.TelephoneNumberDetails ? 'TN Details API (Fallback)' : 'SMS Enabled API (Primary)',
            rawData: smsEnabledData
          });
          
          // Check if SMS is enabled (case-insensitive comparison for "on" and "true")
          // const smsEnabled = smsValue === 'on';//FIXME
          const smsEnabled = smsValue?.toLowerCase() === 'on' || smsValue?.toLowerCase() === 'true';
          
          console.log('🎯 SMS Enablement Check Result:', {
            smsEnabled,
            smsValue,
            expectedValues: ['on', 'ON', 'true', 'TRUE'],
            isMatch: smsValue?.toLowerCase() === 'on' || smsValue?.toLowerCase() === 'true'
          });
          
          if (smsEnabled) {
            console.log("✅ SMS is enabled, campaignId:", campaignId);
            // Set campaignId for later use in campaign status check
            if (campaignId) {
              this.campaignId = campaignId;
              console.log('🎯 Set campaignId for campaign status check:', campaignId);
              console.log('🎯 this.campaignId after setting:', this.campaignId);
              console.log('🎯 this.campaignId type:', typeof this.campaignId);
              console.log('🎯 this.campaignId length:', this.campaignId ? this.campaignId.length : 'N/A');
            } else {
              console.log('⚠️ WARNING: SMS is enabled but campaignId is missing/falsy!');
              console.log('⚠️ campaignId value:', campaignId);
              console.log('⚠️ campaignId type:', typeof campaignId);
              console.log('⚠️ this.campaignId before setting:', this.campaignId);
            }
            
            return {
              title: 'SMS Enablement',
              status: 'success',
              message: 'SMS is enabled for this number',
              data: {
                status: 'Inservice',
                sms: smsValue, // Use actual value from response
                accountId,
                orderId,
                campaignId
              }
            };
          } else {
            console.log("❌ SMS is not enabled");
            return {
              title: 'SMS Enablement',
              status: 'failed',
              message: 'SMS not enabled for this number',
              data: {
                phoneNumber,
                status,
                accountId,
                orderId,
                smsValue,
                expectedValues: ['on', 'ON', 'true', 'TRUE'],
                actualValue: smsValue,
                note: 'Case-insensitive comparison for "on" and "true" values'
              }
            };
          }
        } catch (smsEnabledError: any) {
          console.error('❌ Error checking SMS enabled status:', smsEnabledError);
          return {
            title: 'SMS Enablement',
            status: 'failed',
            message: 'SMS not enabled for this number',
            data: {
              phoneNumber,
              status,
              accountId,
              orderId,
              error: smsEnabledError.message || 'Unknown error',
              errorType: 'API Error'
            }
          };
        }
      } else {
        console.log("❌ Status is not Inservice:", status);
        return {
          title: 'SMS Enablement',
          status: 'failed',
          message: 'SMS not enabled for this number',
          data: {
            phoneNumber,
            status: status || 'Unknown',
            accountId,
            expectedStatus: 'Inservice',
            actualStatus: status,
            note: 'Case-insensitive comparison for status values'
          }
        };
      }
    } catch (error: any) {
      console.error('❌ Error checking SMS enablement:', error);
      
      // Check if it's a specific API error response
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      const errorStatus = error.response?.data?.status;
      const errorCode = error.response?.data?.errorCode || error.response?.status;

      // If API returns a specific error with status "failed", treat as failure
      if (errorStatus?.toLowerCase() === 'failed' || errorMessage.includes('Could not reach the Carrier service')) {
        return {
          title: 'SMS Enablement',
          status: 'failed',
          // Show the carrier's own reason rather than assuming SMS is disabled
          message: errorMessage,
          data: {
            phoneNumber,
            error: errorMessage,
            errorType: 'API Error',
            errorCode: errorCode || 'Unknown',
            status: 'failed'
          }
        };
      }
      
      // For other network/connection errors, treat as warning
      return {
        title: 'SMS Enablement',
        status: 'warning',
        message: 'Error checking SMS enablement',
        data: {
          phoneNumber,
          error: errorMessage,
          errorType: 'Network/API Error'
        }
      };
    }
  }

  private async checkRecentErrors(phoneNumber: string, destinationNumber?: string): Promise<DiagnosticResult> {
    try {
      const response = await diagnosticAPI.getRecentErrors(phoneNumber, destinationNumber);
      
      // Check if the API call was successful
      if (response.data?.status === 'success' && response.data?.data) {
        const usageData = response.data.data;
        
        // Analyze the usage data
        if (usageData && usageData.length > 0) {
          // Calculate total message transactions and analyze patterns
          const totalMessages = usageData.length; // Count number of message transactions
          
          const outboundMessages = usageData.filter((item: any) => 
            item.direction?.toLowerCase() === 'outbound' && parseInt(item.message_count || '0') > 0);
          
          const inboundMessages = usageData.filter((item: any) => 
            item.direction?.toLowerCase() === 'inbound' && parseInt(item.message_count || '0') > 0);
          
          if (totalMessages > 0) {
            return {
              title: 'Recent Error Analysis',
              status: 'success',
              message: 'Messages successfully sent in last 7 days. No errors found',
              data: {
                phoneNumber,
                destinationNumber,
                totalMessages,
                outboundMessages: outboundMessages.length,
                inboundMessages: inboundMessages.length,
                lastCheck: new Date().toISOString(),
                usageData: usageData
              }
            };
          } else {
            return {
              title: 'Recent Error Analysis',
              status: 'warning',
              message: 'No messages found in last 7 days',
              data: {
                phoneNumber,
                destinationNumber,
                totalMessages: 0,
                outboundMessages: outboundMessages.length,
                inboundMessages: inboundMessages.length,
                lastCheck: new Date().toISOString(),
                note: 'No messages found in the last 7 days'
              }
            };
          }
        } else {
          // No usage data available - initialize empty arrays
          const outboundMessages: any[] = [];
          const inboundMessages: any[] = [];
          
          return {
            title: 'Recent Error Analysis',
            status: 'warning',
            message: 'No messages found in last 7 days',
            data: {
              phoneNumber,
              destinationNumber,
              totalMessages: 0,
              outboundMessages: outboundMessages.length,
              inboundMessages: inboundMessages.length,
              lastCheck: new Date().toISOString(),
              note: 'No message data available for analysis'
            }
          };
        }
      } else if (response.data?.status === 'failed') {
        // API call failed
        return {
          title: 'Recent Error Analysis',
          status: 'error',
          message: `Error analysis failed: ${response.data?.error || 'Unknown error'}`,
          data: {
            phoneNumber,
            destinationNumber,
            error: response.data?.error || 'Unknown error',
            errorType: 'Usage API Error'
          }
        };
      } else {
        // No data returned - initialize empty arrays
        const outboundMessages: any[] = [];
        const inboundMessages: any[] = [];
        
        return {
          title: 'Recent Error Analysis',
          status: 'warning',
          message: 'No messages found in last 7 days',
          data: {
            phoneNumber,
            destinationNumber,
            totalMessages: 0,
            outboundMessages: outboundMessages.length,
            inboundMessages: inboundMessages.length,
            lastCheck: new Date().toISOString(),
            note: 'No message data available for analysis'
          }
        };
      }
    } catch (error: any) {
      console.error('❌ Error checking recent errors:', error);
      
      // Extract actual error information
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const errorCode = error.response?.status || 'Unknown';
      
      return {
        title: 'Recent Error Analysis',
        status: 'error',
        message: `Error analysis failed: ${errorMessage}`,
        data: {
          phoneNumber,
          destinationNumber,
          error: errorMessage,
          errorCode: errorCode,
          errorType: 'Recent Error Analysis API Error'
        }
      };
    }
  }

  private async checkCampaignStatus(): Promise<DiagnosticResult> {
    console.log('🔍 Campaign Status Check (called after SMS status) - campaignId:', this.campaignId);
    
    if (!this.campaignId) {
      console.log('❌ No campaignId available for campaign status check');
      return {
        title: '10DLC Campaign Status',
        status: 'failed',
        message: 'Campaign ID not available for status check',
        data: {
          campaignId: null,
          error: 'No campaign ID available',
          errorType: 'Missing Campaign ID'
        }
      };
    }

    try {
      const response = await diagnosticAPI.getCampaignStatus(this.campaignId);
      
      if (response.data?.status?.toLowerCase() === 'active') {
        return {
          title: '10DLC Campaign Status',
          status: 'success',
          message: 'Campaign is active',
          data: response.data
        };
      } else {
        return {
          title: '10DLC Campaign Status',
          status: 'warning',
          message: 'Campaign pending DCA approval',
          data: response.data || {
            campaignId: this.campaignId,
            status: 'UNKNOWN',
            error: 'No response data from API'
          }
        };
      }
    } catch (error: any) {
      console.error('❌ Error checking campaign status:', error);
      
      // Extract actual error information
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const errorCode = error.response?.status || 'Unknown';
      
      return {
        title: '10DLC Campaign Status',
        status: 'failed',
        message: `Campaign status check failed: ${errorMessage}`,
        data: {
          campaignId: this.campaignId,
          error: errorMessage,
          errorCode: errorCode,
          errorType: 'Campaign Status API Error'
        }
      };
    }
  }

  private async checkCarrierApproval(): Promise<DiagnosticResult> {
    console.log('🔍 Carrier Approval Check (called after SMS status) - campaignId:', this.campaignId);
    
    if (!this.campaignId) {
      return {
        title: 'Carrier Approval Status',
        status: 'failed',
        message: 'No campaign ID available for carrier approval check',
        data: {
          campaignId: null,
          error: 'No campaign ID available',
          errorType: 'Missing Campaign ID'
        }
      };
    }

    try {
      console.log('🔍 Carrier Approval Step: Calling DCA status for campaign:', this.campaignId);
      const response = await diagnosticAPI.getCarrierApprovalStatus(this.campaignId);
      
      console.log('📊 DCA Status Response:', response.data);
      
      // Handle the new response format from Flask server
      if (response.data && response.data.status) {
        const dcaResult = response.data;
        
        if (dcaResult.status === 'success') {
          return {
            title: 'Carrier Approval Status',
            status: 'success',
            message: dcaResult.message || 'All DCAs approved',
            data: {
              details: dcaResult.details,
              dcaData: dcaResult.data
            }
          };
        } else {
          return {
            title: 'Carrier Approval Status',
            status: 'failed',
            message: dcaResult.message || 'Some DCAs are not approved',
            data: {
              campaignId: this.campaignId,
              details: dcaResult.details,
              dcaData: dcaResult.data
            }
          };
        }
      } else {
        // Fallback for old response format
        const allRegistered = Object.values(response.data).every(status => 
          typeof status === 'string' && status.toLowerCase() === 'registered');
        
        if (allRegistered) {
          return {
            title: 'Carrier Approval Status',
            status: 'success',
            message: 'All DCAs approved',
            data: {
              details: 'All DCAs are REGISTERED',
              dcaData: response.data
            }
          };
        } else {
          // Find which DCAs are not registered
          const nonRegistered = Object.entries(response.data)
            .filter(([_, status]) => typeof status === 'string' && status.toLowerCase() !== 'registered')
            .map(([dcaId, status]) => `DCA ${dcaId}: ${status}`);
          
          return {
            title: 'Carrier Approval Status',
            status: 'failed',
            message: 'Some DCAs are not approved',
            data: {
              campaignId: this.campaignId,
              details: `Non-registered DCAs: ${nonRegistered.join(', ')}`,
              dcaData: response.data,
              nonRegisteredDCAs: nonRegistered
            }
          };
        }
      }
    } catch (error: any) {
      console.error('❌ Error checking carrier approval:', error);
      return {
        title: 'Carrier Approval Status',
        status: 'error',
        message: 'Error checking DCA approval status',
        data: {
          campaignId: this.campaignId,
          error: error.message,
          errorType: 'API Error',
          possibleReasons: [
            'TCR API service unavailable',
            'Invalid campaign ID',
            'Network connectivity issues',
            'Authentication problems'
          ],
          requiredFields: {
            campaignId: this.campaignId || 'Missing',
            apiAccess: 'TCR API must be accessible',
            authentication: 'Valid credentials required'
          },
          recommendations: [
            'Check TCR API service status',
            'Verify campaign ID is valid',
            'Check network connectivity',
            'Contact support for assistance'
          ]
        }
      };
    }
  }

  // private async checkDeliveryPath(phoneNumber: string): Promise<DiagnosticResult> {
  //   try {
  //     const response = await diagnosticAPI.getDeliveryPath(phoneNumber);
  //     
  //     if (response.data) {
  //       return {
  //         title: 'Delivery Path Analysis',
  //         status: 'success',
  //         message: 'Delivery path clear',
  //         data: {
  //           route: 'ContextSMS -> Bandwidth -> Carrier',
  //           lastCheck: '2024-07-01 11:19:00'
  //         }
  //       };
  //     } else {
  //       return {
  //         title: 'Delivery Path Analysis',
  //         status: 'failed',
  //         message: 'No delivery path available',
  //       };
  //     }
  //   } catch (error) {
  //     return {
  //       title: 'Delivery Path Analysis',
  //       status: 'success',
  //       message: 'Delivery path clear',
  //       data: {
  //         route: 'ContextSMS -> Bandwidth -> Carrier',
  //         lastCheck: '2024-07-01 11:19:00'
  //       }
  //     };
  //   }
  // }

  async runMessageTest(messageData: {
    fromNumber: string;
    toNumber: string;
    message: string;
    groupChannelId?: string; // Now optional since backend constructs it
    platformType: string;
    sentBy: string;
  }, diagnosticResults?: DiagnosticResult[], userEmail?: string): Promise<MessageTestResult> {
    try {
      console.log('📱 Sending test message to Teams backend:', messageData);
      const response = await diagnosticAPI.sendTestMessage(messageData);
      
      console.log('📊 Response data:', response.data);
      
      // Check if the response contains error indicators
      const responseData = response.data;
      const hasError = responseData?.message?.includes('not a valid') || 
                      responseData?.fields?.includes('GroupChannel') ||
                      responseData?.error ||
                      responseData?.status === 'failed';
      
      if (hasError) {
        console.error('❌ Backend returned error response:', responseData);
        
        const messageTestResult = {
          status: 'failed' as const,
          messageId: `MSG-${Date.now()}`,
          deliveryTime: '0 seconds',
          // carrier: 'Unknown',  // Commented out - not showing carrier
          timestamp: new Date().toLocaleString(),
          error: responseData?.message || responseData?.error || 'Unknown error from backend'
        };

        // Create Zendesk ticket for failed message test
        if (diagnosticResults) {
          console.log('🎫 Creating Zendesk ticket after failed message test');
          const zendeskResult = await this.createZendeskTicket(messageData.fromNumber, diagnosticResults, messageTestResult, userEmail, messageData.toNumber);
          
          if (zendeskResult.success) {
            console.log('✅ Zendesk ticket created successfully:', zendeskResult.ticketId);
          } else {
            console.warn('⚠️ Failed to create Zendesk ticket:', zendeskResult.error);
          }
        }
        
        return messageTestResult;
      }
      
      console.log('✅ Test message sent successfully:', response.data);
      
      const messageTestResult = {
        status: 'success' as const,
        messageId: response.data?.messageId || `MSG-${Date.now()}`,
        deliveryTime: response.data?.deliveryTime || '1.2 seconds',
        // carrier: response.data?.carrier || 'Unknown',  // Commented out - not showing carrier
        timestamp: new Date().toLocaleString(),
      };

      // Zendesk ticket already created at Step 3 - no need to create again
      console.log('ℹ️ Skipping Zendesk ticket creation - already created at Step 3');
      
      return messageTestResult;
    } catch (error: any) {
      console.error('❌ Error sending test message:', error);
      
      const messageTestResult = {
        status: 'failed' as const,
        messageId: `MSG-${Date.now()}`,
        deliveryTime: '0 seconds',
        // carrier: 'Unknown',  // Commented out - not showing carrier
        timestamp: new Date().toLocaleString(),
        error: error.response?.data?.message || error.message || 'Network error'
      };

      // Zendesk ticket already created at Step 3 - no need to create again
      console.log('ℹ️ Skipping Zendesk ticket creation - already created at Step 3');
      
      return messageTestResult;
    }
  }

  generateTicketId(): string {
    return `MSG-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  }

  private createDiagnosticReport(phoneNumber: string, diagnosticResults: DiagnosticResult[], messageTestResult?: MessageTestResult): string {
    const reportContent = `Messaging Diagnostic Report
==========================
Generated: ${new Date().toLocaleString()}
Phone Number: ${phoneNumber}
Ticket ID: ${this.generateTicketId()}

DIAGNOSTIC RESULTS:
${diagnosticResults.map(result => 
  `- ${result.title}: ${result.status.toUpperCase()} - ${result.message}`
).join('\n')}

${messageTestResult ? `
MESSAGE TEST RESULTS:
- Status: ${messageTestResult.status.toUpperCase()}
- Message ID: ${messageTestResult.messageId}
- Delivery Time: ${messageTestResult.deliveryTime}
- Carrier: ${messageTestResult.carrier}
- Timestamp: ${messageTestResult.timestamp}
` : ''}

DETAILED DIAGNOSTIC DATA:
${diagnosticResults.map(result => 
  `${result.title}:
  Status: ${result.status}
  Message: ${result.message}
  ${result.data ? `Data: ${JSON.stringify(result.data, null, 2)}` : ''}`
).join('\n\n')}`;

    return reportContent;
  }

  private createTicketSummary(diagnosticResults: DiagnosticResult[], messageTestResult?: MessageTestResult): {
    summary: string;
    rootCauses: string[];
    ticketStatus: 'open' | 'closed';
    stepAnalysis: string[];
  } {
    const failedResults = diagnosticResults.filter(result => 
      result.status === 'failed' || result.status === 'error'
    );
    
    const messageTestFailed = messageTestResult?.status === 'failed';
    const hasIssues = failedResults.length > 0 || messageTestFailed;
    
    // Step-by-step analysis
    const stepAnalysis = [
      '🔍 STEP 1: Number Mapping Check',
      '   • Purpose: Verify phone number is properly mapped to messaging platform',
      '   • Status: ' + (diagnosticResults.find(r => r.title === 'Number Mapping Check')?.status.toUpperCase() || 'UNKNOWN'),
      '   • Impact: Required for message routing',
      '',
      '📱 STEP 2: SMS Enablement Check',
      '   • Purpose: Confirm SMS capabilities are active on the phone number',
      '   • Status: ' + (diagnosticResults.find(r => r.title === 'SMS Enablement')?.status.toUpperCase() || 'UNKNOWN'),
      '   • Impact: Required for SMS delivery',
      '',
      '⚠️ STEP 3: Recent Error Analysis',
      '   • Purpose: Check for recent delivery failures or errors',
      '   • Status: ' + (diagnosticResults.find(r => r.title === 'Recent Error Analysis')?.status.toUpperCase() || 'UNKNOWN'),
      '   • Impact: Identifies ongoing delivery issues',
      '',
      '📋 STEP 4: 10DLC Campaign Status',
      '   • Purpose: Verify 10DLC campaign compliance and registration',
      '   • Status: ' + (diagnosticResults.find(r => r.title === '10DLC Campaign Status')?.status.toUpperCase() || 'UNKNOWN'),
      '   • Impact: Required for A2P messaging compliance',
      '',
      '✅ STEP 5: Carrier Approval Status',
      '   • Purpose: Check Dynamic Campaign Approval (DCA) status',
      '   • Status: ' + (diagnosticResults.find(r => r.title === 'Carrier Approval Status')?.status.toUpperCase() || 'UNKNOWN'),
      '   • Impact: Required for carrier acceptance',
      '',
      '📤 STEP 6: Message Test',
      '   • Purpose: End-to-end message delivery verification',
      '   • Status: ' + (messageTestResult?.status.toUpperCase() || 'UNKNOWN'),
      '   • Impact: Final verification of message delivery'
    ];

    // Root cause analysis
    const rootCauses: string[] = [];
    
    failedResults.forEach(result => {
      switch (result.title) {
        case 'Number Mapping Check':
          rootCauses.push('❌ ROOT CAUSE: Phone number not mapped to messaging platform');
          rootCauses.push('   • Solution: Contact system administrator to map the number');
          rootCauses.push('   • Impact: Messages cannot be routed to correct destination');
          break;
        case 'SMS Enablement':
          rootCauses.push('❌ ROOT CAUSE: SMS capabilities not enabled on phone number');
          rootCauses.push('   • Solution: Enable SMS on the phone number through carrier');
          rootCauses.push('   • Impact: SMS messages will not be delivered');
          break;
        case 'Recent Error Analysis':
          rootCauses.push('❌ ROOT CAUSE: Recent delivery errors detected');
          rootCauses.push('   • Solution: Review error logs and carrier status');
          rootCauses.push('   • Impact: Ongoing delivery failures');
          break;
        case '10DLC Campaign Status':
          rootCauses.push('❌ ROOT CAUSE: 10DLC campaign not compliant or inactive');
          rootCauses.push('   • Solution: Update campaign registration or compliance');
          rootCauses.push('   • Impact: Messages may be blocked by carriers');
          break;
        case 'Carrier Approval Status':
          rootCauses.push('❌ ROOT CAUSE: Dynamic Campaign Approval (DCA) not registered');
          rootCauses.push('   • Solution: Complete DCA registration with carriers');
          rootCauses.push('   • Impact: Messages rejected by carriers');
          break;
      }
    });

    if (messageTestFailed) {
      rootCauses.push('❌ ROOT CAUSE: Message test failed - end-to-end delivery issue');
      rootCauses.push('   • Solution: Check network connectivity and carrier status');
      rootCauses.push('   • Impact: Messages not reaching destination');
    }

    if (rootCauses.length === 0) {
      rootCauses.push('✅ NO ISSUES DETECTED: All diagnostic checks passed successfully');
      rootCauses.push('   • All systems are functioning normally');
      rootCauses.push('   • Message delivery should work as expected');
    }

    // Create summary
    const summary = `
MESSAGING DIAGNOSTIC SUMMARY
============================

OVERALL STATUS: ${hasIssues ? 'ISSUES DETECTED' : 'ALL SYSTEMS OPERATIONAL'}

DIAGNOSTIC OVERVIEW:
• Total Checks: ${diagnosticResults.length}
• Passed: ${diagnosticResults.filter(r => r.status === 'success').length}
• Failed: ${failedResults.length}
• Message Test: ${messageTestResult?.status.toUpperCase() || 'NOT RUN'}

${hasIssues ? 
`ISSUES FOUND:
${failedResults.map(result => `• ${result.title}: ${result.message}`).join('\n')}
${messageTestFailed ? `• Message Test: Failed to deliver test message` : ''}
` : 
`✅ ALL CHECKS PASSED:
• Number mapping is correctly configured
• SMS capabilities are enabled and active
• No recent delivery errors detected
• 10DLC campaign is compliant and active
• Carrier approvals are in place
• Message test completed successfully
`}

RECOMMENDED ACTIONS:
${hasIssues ? 
`1. Review and resolve the identified issues above
2. Re-run diagnostics after implementing fixes
3. Contact support if issues persist`
: 
`1. No action required - system is functioning normally
2. Monitor for any future issues
3. Contact support if problems arise later`
}
    `.trim();

    return {
      summary,
      rootCauses,
      ticketStatus: hasIssues ? 'open' : 'closed',
      stepAnalysis
    };
  }

  async createZendeskTicket(phoneNumber: string, diagnosticResults: DiagnosticResult[], messageTestResult?: MessageTestResult, userEmail?: string, destinationNumber?: string): Promise<{ success: boolean; ticketId?: string; error?: string }> {
    try {
      console.log('🎫 Creating Zendesk ticket for diagnostic report');
      
      // Create structured payload for better webhook formatting
      const ticketSummary = this.createTicketSummary(diagnosticResults, messageTestResult);
      
      // Create properly formatted content for Zendesk
      const formattedContent = this.createFormattedZendeskContent(phoneNumber, diagnosticResults, messageTestResult, ticketSummary, userEmail, destinationNumber);
      
      // Debug: Log content length and preview
      console.log(`📝 Formatted content length: ${formattedContent.length} characters`);
      console.log(`📋 Content preview (first 200 chars): ${formattedContent.substring(0, 200)}`);
      console.log(`📋 Content preview (last 200 chars): ${formattedContent.substring(formattedContent.length - 200)}`);
      
      // Create a properly formatted payload for Zendesk with content in the right field
      // Send direct HTML content instead of JSON wrapper
      // The webhook.site already handles the subject, so we just send the body content
      const payload = formattedContent;  // Send the content directly, not wrapped in JSON

      console.log('📤 Sending diagnostic report to backend webhook service');
      console.log(`📤 Payload length: ${payload.length} characters`);
      
      const config = (window as any).config;
      const baseURL = `${config.protocol}://${config.server}:${config.port}`;
      const endpoint = config.backend.endpoints.createTicket;
      const response = await fetch(`${baseURL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain; charset=utf-8',
        },
        body: payload  // Send as plain text, not JSON string
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log(`✅ Zendesk ticket created successfully (Ticket ID: ${result.ticketId})`);
        return {
          success: true,
          ticketId: result.ticketId
        };
      } else {
        console.error('❌ Failed to create Zendesk ticket:', result.error);
        return {
          success: false,
          error: result.error || `HTTP ${response.status}: ${response.statusText}`
        };
      }
    } catch (error: any) {
      console.error('❌ Error creating Zendesk ticket:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  private createFormattedZendeskContent(phoneNumber: string, diagnosticResults: DiagnosticResult[], messageTestResult?: MessageTestResult, ticketSummary?: any, userEmail?: string, destinationNumber?: string): string {
    const ticketId = this.generateTicketId();
    const timestamp = new Date().toLocaleString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    
    // Extract enterprise information from Number Mapping Check
    const numberMappingResult = diagnosticResults.find(result => result.title === 'Number Mapping Check');
    const enterpriseInfo = numberMappingResult?.data?.enterpriseId ? {
      enterpriseId: numberMappingResult.data.enterpriseId,
      enterpriseName: numberMappingResult.data.enterpriseName || 'Not available',
      cspId: numberMappingResult.data.cspId || 'Not available'
    } : null;
    
    // Create clean, readable content without complex HTML that gets escaped
    const content = `📋 Messaging Diagnostic Report

Report Details:
- Generated: ${timestamp}
- User Email: ${userEmail || 'Not provided'}
- Phone Number: ${phoneNumber}
- Destination Number: ${destinationNumber || 'Not provided'}
- Ticket ID: ${ticketId}
- Overall Status: ${ticketSummary?.ticketStatus?.toLowerCase() === 'open' ? 'ISSUES DETECTED' : 'ALL SYSTEMS OPERATIONAL'}
${enterpriseInfo ? `
Enterprise Information:
- Enterprise ID: ${enterpriseInfo.enterpriseId}
- Enterprise Name: ${enterpriseInfo.enterpriseName}
- CSP ID: ${enterpriseInfo.cspId}
` : ''}

🔍 Diagnostic Results Summary:
${diagnosticResults.map(result => {
  const statusIcon = result.status === 'success' ? '✅' : result.status === 'failed' ? '❌' : '⚠️';
  return `${statusIcon} ${result.title}
  Status: ${result.status.toUpperCase()}
  Message: ${result.message}`;
}).join('\n\n')}

${messageTestResult ? `
📱 Message Test Results:
Status: ${messageTestResult.status.toUpperCase()}
- Message ID: ${messageTestResult.messageId || 'N/A'}
- Delivery Time: ${messageTestResult.deliveryTime || 'N/A'}
- Timestamp: ${messageTestResult.timestamp || 'N/A'}
${messageTestResult.error ? `- Error: ${messageTestResult.error}` : ''}
` : `
📱 Message Test Results:
Status: NOT YET EXECUTED
- Message test will be available after user clicks "Run Message Test" button
`}

📊 Detailed Analysis:
${diagnosticResults.map(result => {
  const statusIcon = result.status === 'success' ? '✅' : result.status === 'failed' ? '❌' : '⚠️';
  let dataDisplay = '';
  
  // Special handling for Recent Error Analysis to show only specific fields
  if (result.title === 'Recent Error Analysis' && result.data) {
    const { phoneNumber, destinationNumber, totalMessages, outboundMessages, inboundMessages } = result.data;
    dataDisplay = `Data: {
  "phoneNumber": "${phoneNumber}",
  "destinationNumber": "${destinationNumber}",
  "totalMessages": ${totalMessages},
  "outboundMessages": ${outboundMessages},
  "inboundMessages": ${inboundMessages}
}`;
  } else if (result.data) {
    // For other results, show the full data
    dataDisplay = `Data: ${JSON.stringify(result.data, null, 2)}`;
  }
  
  return `
${statusIcon} ${result.title}:
  Status: ${result.status.toUpperCase()}
  Message: ${result.message}
  ${dataDisplay}`;
}).join('\n')}

---
📝 Report Generated by: Messaging Diagnostic Tool`;

    // Debug: Check if content ends properly
    console.log(`🔍 Content ends with: "${content.substring(content.length - 50)}"`);
    console.log(`🔍 Content length: ${content.length} characters`);
    
    return content;
  }
}

export default new DiagnosticService(); 